<template>
  <div class="auth-form">
    <div class="auth-form__intro">
      <p class="auth-eyebrow">Teacher account recovery</p>
      <h1>Forgot your password?</h1>
      <p>Enter your school code and teacher login email. We'll send you a link to set a new password.</p>
    </div>
    <form v-if="!sent" novalidate @submit.prevent="submit">
      <div class="auth-field-group">
        <label class="auth-field-label" for="recovery-school-code">School code</label>
        <v-text-field id="recovery-school-code" v-model.trim="code" outlined placeholder="Enter your school code"
          autocomplete="off" autocapitalize="none" prepend-inner-icon="mdi-domain"
          :error-messages="codeErrors" :hide-details="codeErrors.length === 0" @blur="$v.code.$touch()" />
      </div>
      <div class="auth-field-group auth-field-group--last">
        <label class="auth-field-label" for="recovery-email">Teacher login email</label>
        <v-text-field id="recovery-email" v-model.trim="emailAddress" outlined type="email" placeholder="you@example.com"
          autocomplete="email" inputmode="email" prepend-inner-icon="mdi-email-outline"
          :error-messages="emailErrors" :hide-details="emailErrors.length === 0" @blur="$v.emailAddress.$touch()" />
      </div>
      <v-btn class="auth-primary-button mt-5" block x-large :loading="loading" color="primary" type="submit">
        Send reset link <v-icon right size="20">mdi-arrow-right</v-icon>
      </v-btn>
    </form>
    <div v-else class="auth-form__confirmation" role="status">
      <span class="auth-form__confirmation-icon" aria-hidden="true"><v-icon color="white">mdi-email-check-outline</v-icon></span>
      <h2>Check your email</h2>
      <p>We sent a password reset link to <strong>{{ emailAddress }}</strong>. It should arrive soon. Check your spam folder too.</p>
      <p class="auth-form__confirmation-note">The link expires in 30 minutes.</p>
      <v-btn class="auth-primary-button mt-4" block x-large color="primary" :loading="loading"
        :disabled="secondsRemaining > 0" @click="submit">
        {{ secondsRemaining > 0 ? `Resend available in ${secondsRemaining}s` : 'Resend email' }}
      </v-btn>
      <button type="button" class="auth-text-button auth-form__change-account" @click="changeAccount">Use a different email</button>
    </div>
    <p v-if="requestError" class="auth-form__status auth-form__status--error" role="alert">{{ requestError }}</p>
    <div class="auth-form__secondary">
      <button type="button" class="auth-text-button" @click="$router.push('/login')">Back to sign in</button>
    </div>
  </div>
</template>

<script>
import authService from '../service'
const { validationMixin } = require('vuelidate')
const { required, email } = require('vuelidate/lib/validators')

export default {
  name: 'authentication-forgot-password',
  mixins: [validationMixin],
  data() { return { code: '', emailAddress: '', loading: false, sent: false, requestError: '', secondsRemaining: 0, countdown: null } },
  created() { this.code = localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}code`) || '' },
  beforeDestroy() { this.stopCountdown() },
  validations: { code: { required }, emailAddress: { required, email } },
  computed: {
    codeErrors() { return this.$v.code.$dirty && !this.$v.code.required ? ['School code is required.'] : [] },
    emailErrors() {
      if (!this.$v.emailAddress.$dirty) return []
      if (!this.$v.emailAddress.required) return ['Email is required.']
      return this.$v.emailAddress.email ? [] : ['Enter a valid email address.']
    },
  },
  methods: {
    stopCountdown() {
      if (this.countdown) clearInterval(this.countdown)
      this.countdown = null
    },
    startCountdown(seconds) {
      this.stopCountdown()
      this.secondsRemaining = Math.max(0, Number(seconds) || 60)
      this.countdown = setInterval(() => {
        this.secondsRemaining = Math.max(0, this.secondsRemaining - 1)
        if (!this.secondsRemaining) this.stopCountdown()
      }, 1000)
    },
    changeAccount() {
      this.sent = false
      this.requestError = ''
      this.stopCountdown()
      this.secondsRemaining = 0
    },
    async submit() {
      if (!this.sent) this.$v.$touch()
      if (this.$v.$invalid || this.loading || this.secondsRemaining > 0) return
      this.loading = true
      this.requestError = ''
      try {
        const response = await authService.forgotPassword({ code: this.code, email: this.emailAddress })
        this.sent = true
        this.startCountdown(response.retryAfterSeconds)
      } catch (error) {
        const response = error && error.data
        if (error && error.status === 429 && response && response.retryAfterSeconds) {
          this.sent = true
          this.startCountdown(response.retryAfterSeconds)
        }
        this.requestError = (response && response.message) || 'Unable to send the reset link. Please try again.'
      } finally { this.loading = false }
    },
  },
}
</script>
